import { Box, Flex, Text } from "@chakra-ui/react";
import { ResponsiveSankey } from "@nivo/sankey";
import { useMemo } from "react";
import { toSafariSafeSankey } from "../../../../utils/sankey";
import { getFormattedNumValue } from "../../../../utils";
import { chartTheme } from "../components/Consumption/chartTheme";

const ROOT_COLOR = "#1A5276";
const SCOPE_COLORS = {
  "Scope 1": "#5EB5F3",
  "Scope 2": "#84CC16",
  "Scope 3": "#F6A609",
};

// Palette for product nodes — cycles through distinct hues.
const PRODUCT_PALETTE = [
  "#5EB5F3", "#84CC16", "#F6A609", "#9F7AEA", "#FC8181",
  "#4FD1C5", "#F6AD55", "#68D391", "#63B3ED", "#B794F4",
];

// Single-year Sankey. Two modes:
//  Scope mode (default): scope1/scope2/scope3 → three scope nodes.
//  Product mode (products prop): each product → individual product node,
//    sized by its tCO₂e contribution (factor × quantity with unit conversion).
//
// Props:
//  - year: snapshot year label
//  - scope1 / scope2 / scope3: tCO₂e for scope mode
//  - products: array of { description, quantity, unit, totalCo2Emissions } for product mode
//  - rootLabel / subject: copy customisation
const SupplierFlowSankey = ({ year, scope1, scope2, scope3, products, rootLabel = "Total emissions", subject = "this supplier" }) => {
  const isProductMode = Array.isArray(products) && products.length > 0;

  const { nodes, links, labels } = useMemo(() => {
    if (isProductMode) {
      const items = products
        .map((p, i) => {
          const qty = Number(p.quantity) || 0;
          const factor = Number(p.totalCo2Emissions) || 0;
          const value = p.unit === "KG" ? (qty / 1000) * factor : qty * factor;
          const label = p.description || `Product ${i + 1}`;
          return { id: `product-${i}`, label, value, color: PRODUCT_PALETTE[i % PRODUCT_PALETTE.length] };
        })
        .filter((p) => p.value > 0);

      if (items.length === 0) return { nodes: [], links: [], labels: new Map() };

      const builtNodes = [{ id: rootLabel, nodeColor: ROOT_COLOR }, ...items.map((p) => ({ id: p.id, nodeColor: p.color }))];
      const builtLinks = items.map((p) => ({ source: rootLabel, target: p.id, value: Number(p.value.toFixed(2)) }));
      const labelMap = new Map([[rootLabel, rootLabel], ...items.map((p) => [p.id, p.label])]);

      const safe = toSafariSafeSankey(builtNodes, builtLinks);
      return { ...safe, labels: safe.labels.size ? safe.labels : labelMap };
    }

    const scopes = [
      { id: "Scope 1", value: Number(scope1) || 0 },
      { id: "Scope 2", value: Number(scope2) || 0 },
      { id: "Scope 3", value: Number(scope3) || 0 },
    ];
    const total = scopes.reduce((acc, s) => acc + s.value, 0);
    if (total <= 0) return { nodes: [], links: [], labels: new Map() };

    const builtNodes = [{ id: rootLabel, nodeColor: ROOT_COLOR }];
    const builtLinks = [];
    scopes.forEach((s) => {
      if (s.value <= 0) return;
      builtNodes.push({ id: s.id, nodeColor: SCOPE_COLORS[s.id] });
      builtLinks.push({ source: rootLabel, target: s.id, value: Number(s.value.toFixed(2)) });
    });

    return toSafariSafeSankey(builtNodes, builtLinks);
  }, [isProductMode, products, scope1, scope2, scope3, rootLabel]);

  return (
    <Box>
      <Box mb={2}>
        <Text fontSize="body.m" fontWeight="600" color="gray.700">
          Emissions flow
        </Text>
        <Text fontSize="body.xs" color="gray.500">
          {isProductMode
            ? `How ${rootLabel.toLowerCase()} split across individual products for ${year}. Band width is proportional to tCO₂e.`
            : `How ${rootLabel.toLowerCase()} split across Scope 1, 2 and 3 for ${year}. Band width is proportional to tCO₂e.`}
        </Text>
      </Box>

      {nodes.length === 0 ? (
        <Flex
          h="360px"
          align="center"
          justify="center"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          bg="white"
        >
          <Text fontSize="body.s" color="gray.400">
            No emissions data for {year}.
          </Text>
        </Flex>
      ) : (
        <Box h={{ base: "360px", md: "420px" }} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
          <ResponsiveSankey
            data={{ nodes, links }}
            margin={{ top: 16, right: 160, bottom: 16, left: 130 }}
            align="justify"
            sort="input"
            colors={(node) => node.nodeColor}
            nodeThickness={16}
            nodeSpacing={22}
            nodeBorderWidth={0}
            enableLinkGradient
            labelPosition="outside"
            labelOrientation="horizontal"
            labelPadding={12}
            label={(node) => labels.get(node.id) || node.id}
            theme={chartTheme}
            nodeTooltip={({ node }) => (
              <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                <Text fontWeight="700" color={node.nodeColor}>
                  {labels.get(node.id) || node.id}
                </Text>
                <Text color="gray.600">{getFormattedNumValue(Number(node.value), 2)} tCO₂e</Text>
              </Box>
            )}
            linkTooltip={({ link }) => (
              <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                <Text color="gray.700">
                  {labels.get(link.source.id) || link.source.id} → {labels.get(link.target.id) || link.target.id}
                </Text>
                <Text color="gray.600">{getFormattedNumValue(Number(link.value), 2)} tCO₂e</Text>
              </Box>
            )}
          />
        </Box>
      )}

      <Text fontSize="body.xs" color="gray.500" mt={2}>
        {isProductMode
          ? `The widest band shows which product drives most of ${subject}'s footprint — a useful starting point for reduction conversations.`
          : `The widest band is where ${subject}'s footprint is concentrated — usually the best place to target reductions.`}
      </Text>
    </Box>
  );
};

export default SupplierFlowSankey;
